const hasDOM = typeof document !== "undefined";

/**
 * True when the element (a ul/ol/li) belongs to a list tree whose outermost
 * list is a pure bullet list (no ordered list anywhere inside). Those trees
 * survive clipboard sanitization as real <ul> markup instead of being
 * flattened to paragraphs — see isPreservableBulletList.
 */
const isWithinPreservableBulletList = (el: Element): boolean => {
  let outermost = el.closest("ul, ol");
  while (outermost?.parentElement?.closest("ul, ol")) {
    outermost = outermost.parentElement.closest("ul, ol");
  }
  return outermost?.tagName === "UL" && !outermost.querySelector("ol");
};

const sanitizeHtmlForClipboard = (html?: string) => {
  if (!html?.trim()) return "";
  if (typeof DOMParser === "undefined" || !hasDOM) {
    return html;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<body>${html}</body>`, "text/html");
  const body = doc.body;
  if (!body) return html;

  const allowedGlobalAttrs = new Set(["colspan", "rowspan"]);
  const allowedAttributesByTag: Record<string, Set<string>> = {
    A: new Set(["href", "title", "target", "rel"]),
    IMG: new Set(["src", "alt", "title"]),
  };
  const elements = Array.from(body.querySelectorAll("*"));
  elements.forEach((el) => {
    if (!el) return;
    const tagName = el.nodeName;
    const allowedAttrs = allowedAttributesByTag[tagName] || new Set<string>();
    const attrs = Array.from(el.attributes);
    attrs.forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (allowedAttrs.has(name) || allowedGlobalAttrs.has(name)) {
        if (name === "href") {
          if (typeof window === "undefined") {
            el.removeAttribute(attr.name);
            return;
          }
          try {
            const url = new URL(attr.value, window.location.origin);
            if (!["http:", "https:", "mailto:"].includes(url.protocol)) {
              el.removeAttribute(attr.name);
            }
          } catch (_error) {
            el.removeAttribute(attr.name);
          }
        }
        return;
      }
      el.removeAttribute(attr.name);
    });

    if (tagName === "DIV" && !el.getAttributeNames().length) {
      const onlyText = el.childNodes.length === 1 && el.childNodes[0].nodeType === Node.TEXT_NODE;
      if (onlyText) {
        const p = doc.createElement("p");
        p.textContent = el.textContent;
        el.replaceWith(p);
      }
    }

    // Pure bullet lists keep their real <ul>/<li> markup on the clipboard;
    // rich paste targets (Word, Docs, the manuscript editor) parse them into
    // actual lists. Ordered/mixed lists still flatten to paragraphs.
    if ((tagName === "OL" || tagName === "UL") && el.parentElement && !isWithinPreservableBulletList(el)) {
      const listItems = Array.from(el.children).filter((child) => child.tagName === "LI");
      listItems.forEach((li) => {
        const paragraph = doc.createElement("p");
        let content = li.innerHTML;
        if (li.children.length === 1 && li.children[0].tagName === "P") {
          content = li.children[0].innerHTML;
        }
        paragraph.innerHTML = content;
        el.parentElement?.insertBefore(paragraph, el);
      });
      el.remove();
    }

    if (tagName === "LI" && el.parentElement && !isWithinPreservableBulletList(el)) {
      const paragraph = doc.createElement("p");
      let content = el.innerHTML;
      if (el.children.length === 1 && el.children[0].tagName === "P") {
        content = el.children[0].innerHTML;
      }
      paragraph.innerHTML = content;
      el.replaceWith(paragraph);
    }
  });

  Array.from(body.querySelectorAll("hr")).forEach((hr) => {
    hr.remove();
  });

  Array.from(body.querySelectorAll("p")).forEach((node) => {
    const childNodes = Array.from(node.childNodes);
    const hasOnlyBreaks = childNodes.length > 0 && childNodes.every((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        return /^\s*$/.test(child.textContent ?? "");
      }
      return (child as HTMLElement).tagName === "BR";
    });

    const textContent = node.textContent?.replace(/\u00a0/g, "").trim() ?? "";
    const hasMedia = node.querySelector("img");
    if ((!textContent && !hasMedia) || hasOnlyBreaks) {
      node.remove();
    }
  });

  let textNode = body.firstChild;
  while (textNode) {
    if (
      textNode.nodeType === Node.TEXT_NODE &&
      /^\s*$/.test(textNode.textContent ?? "") &&
      textNode.previousSibling?.nodeType === Node.ELEMENT_NODE &&
      textNode.nextSibling?.nodeType === Node.ELEMENT_NODE
    ) {
      const toRemove = textNode;
      textNode = textNode.nextSibling;
      toRemove.parentNode?.removeChild(toRemove);
      continue;
    }
    textNode = textNode.nextSibling;
  }

  if (body.childElementCount === 1) {
    const first = body.firstElementChild;
    if (first && first.tagName === "DIV" && first.getAttributeNames().length === 0) {
      body.innerHTML = first.innerHTML;
    }
  }

  // Don't strip ALL whitespace between tags - only strip between block-level elements
  // Stripping between inline elements like <strong> and <em> removes necessary spaces
  const cleanedHtml = body.innerHTML.trim();
  return cleanedHtml;
};

const normalizePlaintextFromHtml = (html: string, fallback?: string) => {
  if (!html) return fallback ?? "";

  if (!hasDOM) {
    return html.replace(/<[^>]+>/g, "");
  }

  const container = document.createElement("div");
  container.innerHTML = html;

  const nodeWalker = document.createTreeWalker(container, NodeFilter.SHOW_ELEMENT, null);
  let node: Node | null = nodeWalker.currentNode;
  while (node) {
    if (node.nodeName === "HR") {
      const divider = document.createElement("div");
      divider.textContent = "\n\n";
      node.replaceWith(divider);
    }
    node = nodeWalker.nextNode();
  }

  const htmlWithLineBreaks = container.innerHTML
    .replace(/<\/(p|div|li|blockquote|h[1-6])>/gi, "<br><br>")
    .replace(/<br\s*\/?>/gi, "<br>")
    .replace(/(<br>\s*){3,}/gi, "<br><br>");

  const plainWrapper = document.createElement("div");
  plainWrapper.innerHTML = htmlWithLineBreaks;
  const derived = plainWrapper.innerText || plainWrapper.textContent || "";
  return derived
    .replace(/\u00a0/g, " ")
    .replace(/\r\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

export const derivePlainTextFromHtml = (html?: string, fallback?: string) => {
  if (html?.trim()) {
    return normalizePlaintextFromHtml(html, fallback);
  }
  return fallback ?? "";
};

export const copyHtmlToClipboard = async (html?: string, plainText?: string) => {
  const sanitizedHtml = sanitizeHtmlForClipboard(html);
  // Prefer the caller-provided plainText when available — derivePlainTextFromHtml
  // uses innerText on a detached element, which falls back to textContent behavior
  // and loses all <br>/block-level newlines.
  const derivedText = plainText || derivePlainTextFromHtml(sanitizedHtml);
  const hasNavigator = typeof navigator !== "undefined";
  const hasRichClipboardSupport =
    typeof window !== "undefined" &&
    hasNavigator &&
    typeof ClipboardItem !== "undefined" &&
    !!navigator.clipboard?.write;

  if (hasRichClipboardSupport && (sanitizedHtml || derivedText)) {
    try {
      const entries: Record<string, Blob> = {};
      if (sanitizedHtml) {
        entries["text/html"] = new Blob([sanitizedHtml], { type: "text/html" });
      }
      if (derivedText) {
        entries["text/plain"] = new Blob([derivedText], { type: "text/plain" });
      }
      if (Object.keys(entries).length === 0) {
        throw new Error("No clipboard entries");
      }
      const item = new ClipboardItem(entries);
      await navigator.clipboard.write([item]);
      return;
    } catch (_error) {
      // fall through and try plain text
    }
  }

  if (sanitizedHtml && hasDOM && typeof document.execCommand === "function") {
    try {
      const hiddenContainer = document.createElement("div");
      hiddenContainer.style.position = "fixed";
      hiddenContainer.style.pointerEvents = "none";
      hiddenContainer.style.opacity = "0";
      hiddenContainer.style.top = "0";
      hiddenContainer.style.left = "0";
      hiddenContainer.innerHTML = sanitizedHtml;
      document.body.appendChild(hiddenContainer);

      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(hiddenContainer);
      selection?.removeAllRanges();
      selection?.addRange(range);

      const successful = document.execCommand("copy");

      selection?.removeAllRanges();
      hiddenContainer.remove();

      if (successful) {
        return;
      }
    } catch (_error) {
    }
  }

  if (derivedText && hasNavigator && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(derivedText);
  }
};

export const htmlFromSelection = (selection: Selection) => {
  if (!selection?.rangeCount || !hasDOM) return "";

  const contents = document.createElement("div");
  for (let i = 0; i < selection.rangeCount; i += 1) {
    const range = selection.getRangeAt(i).cloneContents();
    contents.appendChild(range);
  }

  return contents.innerHTML;
};

export const getHtmlClipboardPayload = (html?: string) => {
  return sanitizeHtmlForClipboard(html);
};

export const sanitizeClipboardHtml = sanitizeHtmlForClipboard;

const blockLevelTags = new Set(["P", "DIV", "LI", "BLOCKQUOTE", "H1", "H2", "H3", "H4", "H5", "H6", "PRE"]);

export const buildPlainTextFromHtml = (html?: string, fallback?: string): string => {
  if (!html?.trim()) return fallback ?? "";
  if (!hasDOM) {
    return normalizePlaintextFromHtml(html, fallback);
  }

  const container = document.createElement("div");
  container.innerHTML = html;

  const parts: string[] = [];
  let seenBlock = false;

  const append = (text: string) => {
    if (!text) return;
    parts.push(text);
  };

  const ensureParagraphBreak = () => {
    if (!seenBlock) {
      seenBlock = true;
      return;
    }

    if (!parts.length) {
      parts.push("\n\n");
      return;
    }

    const lastIndex = parts.length - 1;
    const last = parts[lastIndex];
    const trailing = last.match(/\n*$/)?.[0]?.length ?? 0;
    const needed = Math.max(0, 2 - trailing);
    if (needed > 0) {
      parts[lastIndex] = last + "\n".repeat(needed);
    }
  };

  const visit = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      append(node.nodeValue ?? "");
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node as HTMLElement;
    if (el.tagName === "BR") {
      append("\n");
      return;
    }

    const isBlock = blockLevelTags.has(el.tagName);
    const beforeLength = parts.length;
    Array.from(el.childNodes).forEach(visit);
    if (isBlock && parts.length > beforeLength) {
      ensureParagraphBreak();
    }
  };

  Array.from(container.childNodes).forEach(visit);

  const processed = parts
    .join("")
    .replace(/\u00a0/g, " ")
    .replace(/\r\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/^\n+/, "")
    .trim();

  return processed || fallback || "";
};

/**
 * Cleans non-breaking spaces from HTML string while preserving list markers
 * This is done on the HTML string level to avoid browser serialization issues
 */
function cleanNonBreakingSpacesFromHtml(html: string): string {
  if (!html) return "";

  // Replace &nbsp; HTML entities and U+00A0 characters with regular spaces
  // BUT preserve them in list markers (e.g., "1.&nbsp;" or "•&nbsp;")
  // and list indentation (multiple &nbsp; in a row)

  let out = html;

  // Protect list markers by replacing them temporarily
  const listMarkerPattern = /(\d+\.|•)&nbsp;/g;
  const indentPattern = /(&nbsp;){2,}/g;
  const protectedMarkers: string[] = [];
  const protectedIndents: string[] = [];

  out = out.replace(listMarkerPattern, (match) => {
    const index = protectedMarkers.length;
    protectedMarkers.push(match);
    return `__LIST_MARKER_${index}__`;
  });

  out = out.replace(indentPattern, (match) => {
    const index = protectedIndents.length;
    protectedIndents.push(match);
    return `__INDENT_${index}__`;
  });

  // Now replace all other &nbsp; and U+00A0 with regular spaces
  out = out.replace(/&nbsp;/g, " ");
  out = out.replace(/\u00A0/g, " ");

  // Restore protected patterns
  protectedIndents.forEach((indent, index) => {
    out = out.replace(`__INDENT_${index}__`, indent);
  });
  protectedMarkers.forEach((marker, index) => {
    out = out.replace(`__LIST_MARKER_${index}__`, marker);
  });

  return out;
}

// Common inline formatting tags that need proper spacing
const INLINE_TAGS = "span|em|strong|b|i|u|mark";

/**
 * Check if a character is alphanumeric (letter or number)
 */
function isAlphanumeric(char: string): boolean {
  if (!char) return false;
  try {
    return /\p{L}|\p{N}/u.test(char);
  } catch (_e) {
    return /[A-Za-z0-9]/.test(char);
  }
}

/**
 * Ensures proper spacing between inline elements
 */
export function ensureInlineSpacing(html: string): string {
  if (!html) return "";
  let out = html;

  // Preserve spaces between inline tags and following content
  out = out.replace(new RegExp(`<\\/(${INLINE_TAGS})>\\s+<(${INLINE_TAGS})`, "gi"), "</$1> <$2");
  out = out.replace(new RegExp(`<\\/(${INLINE_TAGS})>\\s+([^\\s<])`, "gi"), (_m, tag, nextChar) => `</${tag}> ${nextChar}`);

  // Add spacing when inline tags directly abut alphanumeric text
  out = out.replace(new RegExp(`<\\/(${INLINE_TAGS})>(?=([^\\s<]))`, "gi"), (_match, tag, nextChar) => {
    const shouldSpace = isAlphanumeric(nextChar);
    return shouldSpace ? `</${tag}> ` : `</${tag}>`;
  });

  // Add spacing between consecutive inline tags
  out = out.replace(new RegExp(`<\\/(${INLINE_TAGS})><(${INLINE_TAGS})`, "gi"), "</$1> <$2");

  return out;
}

/**
 * Normalizes text by cleaning up whitespace and removing problematic Unicode characters
 */
export function normalizeText(text: string): string {
  if (!text) return "";
  return text
    .replace(/\u00A0/g, " ")
    .replace(/\r\n/g, "\n")
    .replace(/[ \t]+/g, " ")
    .replace(/\s*\n\s*/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Extracts plain text from an array of HTML paragraph strings
 * Separates paragraphs with double newlines and normalizes whitespace
 */
function extractPlainTextFromParagraphs(paragraphs: string[]): string {
  if (!hasDOM) {
    return paragraphs.map((p) => normalizeText(p.replace(/<[^>]+>/g, ""))).join("\n\n");
  }

  return paragraphs
    .map((p) => {
      const temp = document.createElement("div");
      temp.innerHTML = p;
      const text = (temp.textContent || "").trim();
      // Note: normalizeText will clean any remaining nbsp from list markers
      return normalizeText(text);
    })
    .join("\n\n");
}

/**
 * Builds simple HTML from plain text by wrapping paragraphs
 */
export function buildSimpleHtmlFromText(text: string): string {
  if (!text) return "";
  const paragraphs = text
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
  return paragraphs.map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`).join("");
}

function unwrapUnsupportedInsertionElements(root: HTMLElement): void {
  root.querySelectorAll("font").forEach((element) => {
    element.replaceWith(...Array.from(element.childNodes));
  });
}

interface ClipboardPayload {
  plain: string;
  htmlForPaste: string;
}

type PreservedList = { html: string; plain: string };

/**
 * Replaces preservable bullet lists with placeholder paragraphs so the
 * flattening block logic passes them through untouched, returning the cleaned
 * `<ul>` markup (for text/html) and its markdown rendering (for text/plain).
 */
const extractPreservableBulletLists = (container: HTMLElement): PreservedList[] => {
  const preserved: PreservedList[] = [];
  Array.from(container.querySelectorAll("ul"))
    .filter(isPreservableBulletList)
    .forEach((ul) => {
      stripTextMarkersFromListItems(ul);
      const clone = ul.cloneNode(true) as HTMLElement;
      [clone, ...Array.from(clone.querySelectorAll("*"))].forEach((el) => {
        el.getAttributeNames().forEach((name) => el.removeAttribute(name));
      });
      normalizeListWhitespace(clone);
      const placeholder = document.createElement("p");
      placeholder.textContent = listPlaceholderToken(preserved.length);
      preserved.push({ html: clone.outerHTML, plain: serializeListPlainText(ul, 0) });
      ul.replaceWith(placeholder);
    });
  return preserved;
};

/** Swaps the placeholders back for the preserved list markup / markdown. */
const restorePreservedBulletLists = (payload: ClipboardPayload, preserved: PreservedList[]): ClipboardPayload => {
  preserved.forEach((list, index) => {
    const token = listPlaceholderToken(index);
    // Function replacements: list content is user text, and String.replace
    // interprets $-patterns ($&, $$, …) in string replacements.
    payload.htmlForPaste = payload.htmlForPaste.replace(`<p>${token}</p>`, () => list.html);
    payload.plain = payload.plain.replace(token, () => list.plain);
  });
  return payload;
};

/**
 * Builds clipboard payload for regular (non-markdown) content.
 *
 * This function processes HTML content for clipboard operations by:
 * 1. Adding proper spacing between inline elements
 * 2. Extracting paragraph/div blocks
 * 3. Filtering out empty content
 * 4. Building both HTML and plain text representations with spacing between paragraphs
 *
 * Note: This does NOT handle markdown lists - use buildMarkdownClipboardPayload for that.
 */
export function buildClipboardPayload(rawHtml: string, textFallback?: string): ClipboardPayload {
  // Server-side fallback (no DOM available)
  if (!hasDOM) {
    return {
      plain: textFallback || "",
      htmlForPaste: rawHtml || "",
    };
  }

  // Parse HTML - first clean nbsp, then ensure proper spacing between inline elements
  const container = document.createElement("div");
  // Clean nbsp FIRST to avoid double-space issues with ensureInlineSpacing
  const cleanedRawHtml = cleanNonBreakingSpacesFromHtml(rawHtml || "");
  container.innerHTML = ensureInlineSpacing(cleanedRawHtml);

  // Unwrap redundant div wrappers to avoid nesting divs inside paragraphs
  // Example: <div><p>content</p></div> becomes <p>content</p>
  while (
    container.childElementCount === 1 &&
    container.firstElementChild?.tagName === "DIV" &&
    container.firstElementChild.getAttributeNames().length === 0
  ) {
    container.innerHTML = container.firstElementChild.innerHTML;
  }

  // Find all paragraph, div, and heading blocks (top-level first, then search deeper if needed)
  const blockData: Array<{ tag: string; content: string }> = [];
  const isBlockElement = (el: Element): boolean => {
    const tag = el.tagName;
    return tag === "P" || tag === "DIV" || /^H[1-6]$/.test(tag);
  };
  let blocks = Array.from(container.children).filter(isBlockElement);
  if (blocks.length === 0) {
    blocks = Array.from(container.querySelectorAll("p, div, h1, h2, h3, h4, h5, h6"));
  }

  // If there are still no blocks, wrap the content in a paragraph to preserve structure
  if (blocks.length === 0 && container.innerHTML.trim()) {
    const wrapper = document.createElement("p");
    wrapper.innerHTML = container.innerHTML;
    container.innerHTML = "";
    container.appendChild(wrapper);
    blocks = [wrapper];
  }

  // Extract non-empty blocks, filtering out empty paragraphs and those with only <br>
  blocks.forEach((block) => {
    const hasOnlyBr = block.childNodes.length === 1 && block.childNodes[0].nodeName === "BR";
    const text = (block.textContent || "").trim();

    if (!hasOnlyBr && text) {
      // Remove trailing <br> tags and capture the block's HTML
      const content = block.innerHTML.replace(/<br\s*\/?>\s*$/i, "").trim();
      // Preserve heading tags, convert DIV to P
      const tag = /^H[1-6]$/.test(block.tagName) ? block.tagName.toLowerCase() : "p";
      blockData.push({ tag, content });
    }
  });

  let plain = "";
  let htmlForPaste = "";

  if (blockData.length > 0) {
    // Build HTML preserving heading tags. Blocks are clean <p>/<h*> elements
    // with no injected trailing <br>: spacing-as-content used to leak
    // hardBreaks into the editor on paste (the mixed <br>-vs-<p> document
    // state) and doubled blank lines in external apps.
    htmlForPaste = blockData.map((b) => `<${b.tag}>${b.content}</${b.tag}>`).join("");

    // Build plain text by extracting text content from each block
    const paragraphs = blockData.map((b) => b.content);
    plain = extractPlainTextFromParagraphs(paragraphs);
  } else {
    // Fallback: no paragraphs found, use alternative text extraction
    const normalizedHtml = container.innerHTML;
    const fallbackText = textFallback || container.textContent || "";
    const derivedPlain = buildPlainTextFromHtml(normalizedHtml, fallbackText);
    plain = normalizeText(derivedPlain);
    htmlForPaste = buildSimpleHtmlFromText(plain);
  }

  return { plain, htmlForPaste };
}

/**
 * Builds clipboard payload for markdown content with list support.
 *
 * This is similar to buildClipboardPayload but with special handling for markdown lists:
 * 1. Processes <li> elements from <ol> and <ul> tags
 * 2. Adds list markers (1., 2., •) to list items for proper copy/paste
 * 3. Handles nested lists with indentation
 * 4. Respects custom start attributes on ordered lists
 * 5. Skips adding markers if they already exist in the content
 */
export function buildMarkdownClipboardPayload(rawHtml: string, textFallback?: string): ClipboardPayload {
  // Server-side fallback (no DOM available)
  if (!hasDOM) {
    return {
      plain: textFallback || "",
      htmlForPaste: rawHtml || "",
    };
  }

  // Check for partial list selection (orphaned LI tags without parent list)
  // If the HTML has <li> tags but NO <ol> or <ul> tags, check if it has markers
  let processedHtml = rawHtml || "";
  const trimmedHtml = processedHtml.trim();
  const hasLiTags = /<li[\s>]/i.test(trimmedHtml);
  const hasListTags = /<(ol|ul)[\s>]/i.test(trimmedHtml);

  if (hasLiTags && !hasListTags) {
    // Create a temp element to process LIs individually
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = processedHtml;
    const listItems = Array.from(tempDiv.querySelectorAll("li"));

    // Process each LI to determine if it has a marker
    const processedItems: Array<{ hasMarker: boolean; element: HTMLLIElement; markerType?: "ordered" | "unordered"; number?: number }> = [];

    for (const li of listItems) {
      const text = (li.textContent || "").trim();
      const orderedMatch = /^\s*(\d+)\.\s/.exec(text);
      const unorderedMatch = /^\s*[•\-\u2022]\s/.test(text);

      if (orderedMatch) {
        processedItems.push({
          hasMarker: true,
          element: li,
          markerType: "ordered",
          number: parseInt(orderedMatch[1], 10),
        });
      } else if (unorderedMatch) {
        processedItems.push({
          hasMarker: true,
          element: li,
          markerType: "unordered",
        });
      } else {
        processedItems.push({
          hasMarker: false,
          element: li,
        });
      }
    }

    // Rebuild the HTML, grouping consecutive items with markers into lists
    let rebuiltHtml = "";
    let i = 0;

    while (i < processedItems.length) {
      const item = processedItems[i];

      if (!item.hasMarker) {
        // No marker - wrap in <p> tags to ensure it's processed as a paragraph
        rebuiltHtml += `<p>${item.element.innerHTML}</p>`;
        i++;
      } else {
        // Has marker - collect consecutive items with the same marker type
        const markerType = item.markerType;
        const startNumber = item.number;
        const groupItems: typeof processedItems = [];

        while (i < processedItems.length && processedItems[i].hasMarker && processedItems[i].markerType === markerType) {
          groupItems.push(processedItems[i]);
          i++;
        }

        // Wrap the group in the appropriate list tag
        // Keep the marker in the text content since buildMarkdownClipboardPayload expects it
        const listContent = groupItems.map((gi) => {
          const innerHTML = gi.element.innerHTML;
          return `<li>${innerHTML}</li>`;
        }).join("");
        if (markerType === "ordered" && startNumber !== undefined) {
          rebuiltHtml += `<ol start="${startNumber}">${listContent}</ol>`;
        } else {
          rebuiltHtml += `<ul>${listContent}</ul>`;
        }
      }
    }

    processedHtml = rebuiltHtml;
  }

  const container = document.createElement("div");
  // Parse HTML - first clean nbsp, then ensure proper spacing between inline elements
  // Clean nbsp FIRST to avoid double-space issues with ensureInlineSpacing
  const cleanedProcessedHtml = cleanNonBreakingSpacesFromHtml(processedHtml);
  container.innerHTML = ensureInlineSpacing(cleanedProcessedHtml);

  // Unwrap redundant div wrappers to avoid nesting divs inside paragraphs
  while (
    container.childElementCount === 1 &&
    container.firstElementChild?.tagName === "DIV" &&
    container.firstElementChild.getAttributeNames().length === 0
  ) {
    container.innerHTML = container.firstElementChild.innerHTML;
  }

  // Pure bullet lists skip the flattening below entirely: text/html keeps
  // real <ul> markup (rich targets — including our own paste handler — parse
  // it into actual lists) and text/plain gets markdown `- ` markers (which
  // the editor's markdown paste path re-parses into real lists). Flattening
  // to `•` paragraphs would defeat both.
  const preservedLists = extractPreservableBulletLists(container);

  // Find all block-level elements, including list items (LI) and headings (H1-H6)
  const blockData: Array<{ tag: string; content: string }> = [];
  const seenBlocks = new Set<Element>();
  const isBlockCandidate = (el: Element): boolean => {
    const tag = el.tagName;
    return tag === "P" || tag === "DIV" || tag === "LI" || tag === "PRE" || /^H[1-6]$/.test(tag);
  };

  let blocks = Array.from(container.querySelectorAll("p, div, li, pre, h1, h2, h3, h4, h5, h6")).filter((el) => {
    if (el === container) return false;
    if (seenBlocks.has(el)) return false;
    seenBlocks.add(el);
    return true;
  });
  if (blocks.length === 0) {
    blocks = Array.from(container.children).filter((el) => isBlockCandidate(el));
  }

  // If there are still no blocks, wrap the content in a paragraph to preserve structure
  if (blocks.length === 0 && container.innerHTML.trim()) {
    const wrapper = document.createElement("p");
    wrapper.innerHTML = container.innerHTML;
    container.innerHTML = "";
    container.appendChild(wrapper);
    blocks = [wrapper];
  }

  blocks.forEach((block) => {
    // Skip paragraphs/divs that are inside list items (they'll be processed as part of the LI)
    if (block.tagName !== "LI" && block.closest("li")) {
      return;
    }
    // Skip div containers that only hold nested blocks (no direct text content)
    if (block.tagName === "DIV") {
      const hasNestedBlocks = block.querySelector("p, div, li, pre");
      const directText = Array.from(block.childNodes).some((node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim());
      if (hasNestedBlocks && !directText) {
        return;
      }
    }

    // Special handling for list items (LI)
    if (block.tagName === "LI") {
      // Determine if this is an ordered (numbered) or unordered (bulleted) list
      const parentList = block.closest("ol, ul");
      const isOrderedList = parentList?.tagName === "OL";
      const siblings = parentList ? Array.from(parentList.children).filter((child) => child.tagName === "LI") : [];
      const siblingIndex = parentList ? siblings.indexOf(block) : -1;

      // Handle custom start attribute (e.g., <ol start="5">)
      const startAttr = parentList?.getAttribute?.("start");
      const startNumber = startAttr && !Number.isNaN(parseInt(startAttr, 10)) ? parseInt(startAttr, 10) : 1;
      const listNumber = isOrderedList ? startNumber + Math.max(siblingIndex, 0) : null;

      // Calculate nesting depth for indentation
      let listDepth = 0;
      let current = block.parentElement;
      while (current && current !== container) {
        if (current.tagName === "OL" || current.tagName === "UL") {
          listDepth += 1;
        }
        current = current.parentElement;
      }
      const indentUnits = Math.max(0, listDepth - 1);
      const indentHtml = indentUnits > 0 ? "&nbsp;&nbsp;".repeat(indentUnits) : "";

      // Flatten nested paragraphs/divs within the list item into a single line with <br> tags
      let normalizedInner = block.innerHTML
        .replace(/<\/(p|div)>/gi, "<br>")
        .replace(/<(p|div)>/gi, "")
        .replace(/(<br>\s*)+$/gi, "")
        .trim();
      const plainText = (block.textContent || "").trim();
      if (normalizedInner.length === 0) {
        return;
      }

      // Check if the content already has a list marker (e.g., "1. " or "• ")
      // If so, don't add another marker to avoid duplication
      const hasExplicitMarker = /^[\d]+[.)]/.test(plainText) || /^[•\-\u2022]/.test(plainText);
      const markerText = hasExplicitMarker ? "" : isOrderedList && Number.isFinite(listNumber) ? `${listNumber}.` : "•";
      const prefix = markerText ? `${markerText}&nbsp;` : "";

      // Build the final list item HTML with indentation and marker
      const listHtml = `${indentHtml}${prefix}${normalizedInner}`;
      blockData.push({ tag: "p", content: listHtml });
      return;
    }

    // Handle PRE/CODE blocks (fenced code from Markdown)
    if (block.tagName === "PRE") {
      const codeChild = block.querySelector("code");
      const textContent = (codeChild || block).textContent || "";
      if (textContent.trim()) {
        const escapedContent = textContent
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/\n/g, "<br>");
        blockData.push({ tag: "pre", content: escapedContent });
      }
      return;
    }

    // Handle regular paragraphs, divs, and headings
    const hasOnlyBr = block.childNodes.length === 1 && block.childNodes[0].nodeName === "BR";
    const text = (block.textContent || "").trim();

    if (!hasOnlyBr && text) {
      const content = block.innerHTML.replace(/<br\s*\/?>\s*$/i, "").trim();
      // Preserve heading tags, convert DIV to P
      const tag = /^H[1-6]$/.test(block.tagName) ? block.tagName.toLowerCase() : "p";
      blockData.push({ tag, content });
    }
  });

  let plain = "";
  let htmlForPaste = "";

  if (blockData.length > 0) {
    // Build clean HTML preserving heading and preformatted block tags, with no
    // injected trailing <br>; paragraph spacing belongs to the destination.
    htmlForPaste = blockData.map((b) => {
      if (b.tag === "pre") {
        return `<pre><code>${b.content}</code></pre>`;
      }
      return `<${b.tag}>${b.content}</${b.tag}>`;
    }).join("");

    // Build plain text by extracting text content from each block
    const prePlainParts: string[] = [];
    const nonPreParagraphs: string[] = [];
    const blockOrder: Array<{ type: "pre" | "other"; index: number }> = [];
    blockData.forEach((b) => {
      if (b.tag === "pre") {
        blockOrder.push({ type: "pre", index: prePlainParts.length });
        prePlainParts.push(
          b.content
            .replace(/<br>/g, "\n")
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&amp;/g, "&")
        );
      } else {
        blockOrder.push({ type: "other", index: nonPreParagraphs.length });
        nonPreParagraphs.push(b.content);
      }
    });
    const processedNonPre = nonPreParagraphs.length > 0
      ? extractPlainTextFromParagraphs(nonPreParagraphs).split("\n\n")
      : [];
    const allParts: string[] = blockOrder.map((entry) =>
      entry.type === "pre" ? prePlainParts[entry.index] : (processedNonPre[entry.index] || "")
    );
    plain = allParts.join("\n\n");
  } else {
    const normalizedHtml = container.innerHTML;
    const fallbackText = textFallback || container.textContent || "";
    const derivedPlain = buildPlainTextFromHtml(normalizedHtml, fallbackText);
    plain = normalizeText(derivedPlain);
    htmlForPaste = buildSimpleHtmlFromText(plain);
  }

  return restorePreservedBulletLists({ plain, htmlForPaste }, preservedLists);
}

const LIST_PLACEHOLDER_PREFIX = "__SUDOWRITE_PRESERVED_LIST_";
const listPlaceholderToken = (index: number) => `${LIST_PLACEHOLDER_PREFIX}${index}__`;

/**
 * Bullet lists whose subtree contains no ordered list survive copy/insert as
 * real `<ul>` markup instead of being flattened to `•` paragraphs. Ordered
 * (and mixed) lists stay flattened to text markers: the manuscript schema has
 * no orderedList node, so a pasted/inserted `<ol>` would lose its numbering.
 */
const isPreservableBulletList = (ul: Element): boolean =>
  !ul.parentElement?.closest("ul, ol") && !ul.querySelector("ol");

/** Strips `• ` / `1. ` text markers (markdownFormatter renders ordered
 * markers as text; `• ` handling remains for legacy content that still
 * carries literal bullet markers). */
const stripTextMarkersFromListItems = (root: Element) => {
  root.querySelectorAll("li").forEach((li) => {
    const firstNode = li.firstChild;
    if (firstNode && firstNode.nodeType === Node.TEXT_NODE) {
      const text = firstNode.textContent || "";
      const cleanedText = text.replace(/^\s*\d+\.\s*/, "").replace(/^\s*[•\u2022]\s*/, "");
      if (cleanedText !== text) {
        firstNode.textContent = cleanedText;
      }
    }
  });
};

/**
 * Drops the source-formatting whitespace (indentation/newlines between tags)
 * from a preserved list clone so the re-injected markup is clean.
 */
const normalizeListWhitespace = (root: HTMLElement) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  const textNodes: Text[] = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode as Text);
  textNodes.forEach((textNode) => {
    const parentTag = textNode.parentElement?.tagName;
    if ((parentTag === "UL" || parentTag === "OL") && /^\s*$/.test(textNode.textContent || "")) {
      textNode.remove();
      return;
    }
    textNode.textContent = (textNode.textContent || "").replace(/\s+/g, " ");
  });
  root.querySelectorAll("li").forEach((li) => {
    const first = li.firstChild;
    if (first?.nodeType === Node.TEXT_NODE) {
      first.textContent = (first.textContent || "").replace(/^\s+/, "");
    }
    const trimTrailing = (node: ChildNode | null) => {
      if (node?.nodeType === Node.TEXT_NODE) {
        node.textContent = (node.textContent || "").replace(/\s+$/, "");
        if (!node.textContent) node.remove();
      }
    };
    trimTrailing(li.lastChild);
    // Text directly before a nested list also carries source indentation
    li.querySelectorAll(":scope > ul, :scope > ol").forEach((nested) => trimTrailing(nested.previousSibling));
  });
};

/**
 * Plain-text rendering of a preserved list as markdown: `- ` markers with
 * two-space nesting — the same dialect as the manuscript editor's
 * clipboardTextSerializer, so pasting the text back into the editor (or into
 * any markdown-aware app) re-parses it into a real list.
 */
const serializeListPlainText = (list: Element, depth: number): string => {
  const lines: string[] = [];
  Array.from(list.children).forEach((item) => {
    if (item.tagName !== "LI") return;
    const clone = item.cloneNode(true) as HTMLElement;
    clone.querySelectorAll("ul, ol").forEach((nested) => nested.remove());
    const text = normalizeText(clone.textContent || "").replace(/\n/g, " ");
    if (text) lines.push(`${"  ".repeat(depth)}- ${text}`);
    Array.from(item.children)
      .filter((child) => child.tagName === "UL" || child.tagName === "OL")
      .forEach((nested) => lines.push(serializeListPlainText(nested, depth + 1)));
  });
  return lines.join("\n");
};

/**
 * Processes markdown card HTML for insertion by removing duplicate list markers
 * and wrapping content appropriately. This is specifically for the Insert button
 * on markdown cards that use markdownFormatter with custom list components.
 */
export function processMarkdownCardForInsertion(html: string): ClipboardPayload {
  if (!hasDOM) {
    return {
      plain: html,
      htmlForPaste: html,
    };
  }

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  unwrapUnsupportedInsertionElements(tempDiv);

  // Remove the text markers added by markdownFormatter since buildMarkdownClipboardPayload will add them back
  stripTextMarkersFromListItems(tempDiv);

  // Wrap lists in div containers for proper spacing. Only the lists
  // buildMarkdownClipboardPayload flattens (ordered/mixed) — pure bullet
  // lists are preserved as real <ul> markup by the builder itself, so a
  // wrapper inside them would leak into the preserved markup.
  const lists = tempDiv.querySelectorAll("ol, ul");
  lists.forEach((list) => {
    if (isWithinPreservableBulletList(list)) return;
    const wrapper = document.createElement("div");
    list.parentNode?.insertBefore(wrapper, list);
    wrapper.appendChild(list);
  });

  // Process through markdown clipboard payload builder
  const payload = buildMarkdownClipboardPayload(tempDiv.innerHTML);

  // Unwrap the first block to make insertion inline while preserving structure for the rest
  // This prevents the extra line break that occurs when Tiptap inserts block elements at the start
  // Preserve headings, preformatted blocks, and <ul> lists; only a leading
  // paragraph is unwrapped for inline insertion.
  if (payload.htmlForPaste) {
    const inlineDiv = document.createElement("div");
    inlineDiv.innerHTML = payload.htmlForPaste;
    // The builder emits flat block markup: p / pre / h1-h6 / preserved <ul>.
    const blocks = Array.from(inlineDiv.children);

    if (blocks.length > 0) {
      // Insert clean adjacent blocks: no trailing <br> inside a block (TipTap
      // parses that into a hardBreak INSIDE the inserted paragraph — the
      // mixed <br>-vs-<p> document state) and no empty spacer paragraphs
      // between blocks (they render as <p><br></p> in the editor DOM). The
      // visual gap between paragraphs comes from the user's Paragraph
      // Spacing setting, matching what paste does.
      const stripTrailingBreaks = (blockHtml: string) => blockHtml.replace(/(<br\s*\/?>\s*)+$/i, "").trim();
      const rewrap = (b: Element) => {
        // Preserved bullet lists pass through with their markup intact
        if (b.tagName === "UL") return b.outerHTML;
        const tag = b.tagName.toLowerCase();
        return `<${tag}>${stripTrailingBreaks(b.innerHTML)}</${tag}>`;
      };

      const firstBlock = blocks[0];
      // Headings and lists stay block-level; only a leading paragraph is
      // unwrapped so insertion starts inline at the cursor
      const firstContent = firstBlock.tagName === "P" ? stripTrailingBreaks(firstBlock.innerHTML) : rewrap(firstBlock);
      payload.htmlForPaste = `${firstContent}${blocks.slice(1).map(rewrap).join("")}`;
    }
  }

  return payload;
}
